import { FC } from 'react'
import { FormikProps } from 'formik'

import FormikField from '../../form/FormikField'
import { FormikFormProps } from '../../form/FormikForm'
import { FormFieldInputProps } from '../../form/fields/FormFieldInput'
import { ActionButtonVariant } from '../../components/ActionButton/styled'
import { FormSubmitButtonProps } from '../../form/FormSubmitButton'
import FormMessages, { FormMessagesProps } from '../../form/FormMessages'
import { FormFieldEmailProps } from '../../form/fields/FormFieldEmail'
import { FormFieldSelectProps } from '../../form/fields/FormFieldSelect'
import { FormFieldOrientation } from '../../types/form'
import { FormFieldPhoneProps } from '../../form/fields/FormFieldPhone'
import { FormFieldCheckboxProps } from '../../form/fields/FormFieldCheckbox'
import { FormFieldTextAreaProps } from '../../form/fields/FormFieldTextArea'
import { ProfilesUserId } from '../../rest/types/UserProfile'

import * as SC from './styled'

type RegisterFormValues = {
  civility: string
  firstName: string
  lastName: string
  email: string
  profile: string
  speciality: string
  password: string
  requestAccess: boolean
  numberRpps: string
  phone: string
  installationProject: string
}

export type RegisterFormProps = FormikFormProps<RegisterFormValues> & {
  fields: {
    civility: Omit<FormFieldSelectProps, 'name'>
    firstName: Omit<FormFieldInputProps, 'name' | 'type'>
    lastName: Omit<FormFieldInputProps, 'name' | 'type'>
    email: Omit<FormFieldEmailProps, 'name'>
    profile: Omit<FormFieldSelectProps, 'name'>
    speciality?: Omit<FormFieldSelectProps, 'name'>
    password: Omit<FormFieldInputProps, 'name' | 'type'>
    requestAccess: Omit<FormFieldCheckboxProps, 'name'>
    numberRpps?: Omit<FormFieldInputProps, 'name' | 'type'>
    phone?: Omit<FormFieldPhoneProps, 'name'>
    installationProject?: Omit<FormFieldTextAreaProps, 'name'>
  }
  title: string
  requestAccessTitle: string
  requestAccessText: string
  submitButton?: FormSubmitButtonProps
  success?: string
  errors?: FormMessagesProps['messages']
}

const RegisterForm: FC<RegisterFormProps> = ({
  fields,
  submitButton,
  success,
  title,
  requestAccessTitle,
  requestAccessText,
  errors,
  ...formikProps
}) => {
  const {
    firstName,
    lastName,
    email,
    profile,
    civility,
    speciality,
    password,
    requestAccess,
    numberRpps,
    phone,
    installationProject,
  } = fields

  return (
    <SC.Container>
      {!success && (
        <>
          <SC.Title>{title}</SC.Title>
          <SC.Form {...formikProps}>
            {(values: FormikProps<RegisterFormValues>) => {
              return (
                <>
                  <SC.Fields>
                    <SC.Group3>
                      <FormikField
                        orientation={FormFieldOrientation.Horizontal}
                        {...civility}
                        name="civility"
                        innerError
                        Component={SC.SelectField}
                      />
                      <FormikField
                        labelProps={{ underlined: true }}
                        orientation={FormFieldOrientation.Horizontal}
                        {...firstName}
                        name="firstName"
                        type="text"
                        innerError
                        Component={SC.NameField}
                      />
                      <FormikField
                        labelProps={{ underlined: true }}
                        orientation={FormFieldOrientation.Horizontal}
                        {...lastName}
                        type="text"
                        name="lastName"
                        innerError
                        Component={SC.TextField}
                      />
                    </SC.Group3>
                    <SC.Group>
                      <FormikField
                        orientation={FormFieldOrientation.Horizontal}
                        {...profile}
                        name="profile"
                        innerError
                        Component={SC.SelectFieldProfile}
                      />
                      {(values?.values?.profile === ProfilesUserId.doctor ||
                        values?.values?.profile === ProfilesUserId.intern) && (
                        <FormikField
                          orientation={FormFieldOrientation.Horizontal}
                          {...speciality}
                          name="speciality"
                          innerError
                          Component={SC.SelectFieldSpeciality}
                        />
                      )}
                    </SC.Group>
                    <SC.Group>
                      <FormikField
                        labelProps={{ underlined: true }}
                        orientation={FormFieldOrientation.Horizontal}
                        {...email}
                        name="email"
                        innerError
                        Component={SC.EmailField}
                      />
                      <FormikField
                        labelProps={{ underlined: true }}
                        orientation={FormFieldOrientation.Horizontal}
                        name="password"
                        type="password"
                        innerError
                        {...password}
                        Component={SC.TextField}
                      />
                    </SC.Group>
                  </SC.Fields>
                  <SC.RequestAccessTitle>
                    {requestAccessTitle}
                  </SC.RequestAccessTitle>
                  <FormikField
                    labelProps={{ underlined: true }}
                    orientation={FormFieldOrientation.Horizontal}
                    name="requestAccess"
                    innerError
                    {...requestAccess}
                    Component={SC.CheckboxField}
                  />
                  <SC.RequestAccessText>
                    {requestAccessText}
                  </SC.RequestAccessText>
                  {values?.values?.requestAccess && (
                    <SC.RequestAccessFormBlock>
                      <SC.Group2>
                        <FormikField
                          name="numberRpps"
                          innerError
                          {...numberRpps}
                          Component={SC.TextField}
                        />
                        <FormikField
                          name="phone"
                          innerError
                          {...phone}
                          Component={SC.PhoneField}
                        />
                      </SC.Group2>
                      <FormikField
                        name="installationProject"
                        innerError
                        {...installationProject}
                        Component={SC.TextareaField}
                      />
                    </SC.RequestAccessFormBlock>
                  )}
                  {errors && <FormMessages messages={errors} />}
                  <SC.Actions>
                    <SC.SubmitButton
                      type={'submit'}
                      variant={ActionButtonVariant.quaternary}
                      {...submitButton}
                    />
                  </SC.Actions>
                </>
              )
            }}
          </SC.Form>
        </>
      )}
      {success && <SC.Success>{success}</SC.Success>}
    </SC.Container>
  )
}

export default RegisterForm
